import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
// import binIcon from '@/assets/icons/bin.svg';
import infoIcon from '@/assets/icons/info.svg';

// services
import { accountingCenterService } from '@/services/accounting-centers.service';

// utils
import { withPaymentAmountFromAcAllocation } from '@/utils/invoiceAllocationAmount';
import { getACGeneralInfoMetrics } from '@/utils/accountingCenterGeneralInfo';
import { AC_OVERVIEW_PROGRESS_COLORS, getACOverviewProgressMetrics } from '@/utils/accountingCenterOverviewProgress';

// components
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import InfoModal from '@/components/molecules/InfoModal';
import ACGeneralInfoAccordion from '@/components/molecules/ACGeneralInfoAccordion';
import ACOverviewProgressCard from '@/components/molecules/accountingCenter/ACOverviewProgressCard';

// icons
import { ReactComponent as DepartmentACIcon } from '@/assets/icons/department-acs.svg';
import noInvoiceIcon from '@/assets/icons/no-invoice.svg';
import noWorkActivityIcon from '@/assets/icons/no-work-activity.svg';
import binIcon from '@/assets/icons/bin.svg';
import editIcon from '@/assets/icons/edit.svg';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import invoiceIcon from '@/assets/icons/invoice.svg';
import invoiceWhiteIcon from '@/assets/icons/invoice-white.svg';
import workActivityIcon from '@/assets/icons/work-activity-primary.svg';
import workActivityWhiteIcon from '@/assets/icons/work-activity-white.svg';
import deleteBankAccountIcon from '@/assets/icons/delete-bankAccount.svg';
import ExplanatoryModal from '@/components/molecules/ExplanatoryModal';

import acExpanatoryImg from '@/assets/images/ac-explanatory-1.png';
import acExpanatoryImg2 from '@/assets/images/ac-explanatory-2.png';
import acExpanatoryImg3 from '@/assets/images/ac-explanatory-3.png';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import { Invoice } from '@/services/types/invoice.types';
import { invoiceService } from '@/services/invoice.service';
import { workActivityService } from '@/services/work-activity.service';
import TabPills from '@/components/molecules/TabPills';
import InvoicesTable from '@/components/molecules/invoice/InvoicesTable';
import WorkActivityTable from '@/components/molecules/workActivity/WorkActivityTable';
import { WorkActivity } from '@/services/types/work-activity.types';
import { useBudgetPermissions } from '@/hooks/useBudgetPermissions';
import { PAGE } from '@/routes';

type ContentTab = 'invoice' | 'work-activity';

const steps = [
  {
    title: 'Understanding MAEs',
    message:
      'MAEs are customizable entities designed for better cashflow management. They are categorized into four types: Departments, Products & Services, Projects, and Contract Entities.',
    img: acExpanatoryImg
  },
  {
    title: 'Setting Limits with ACMEV and ACRT',
    message:
      'ACMEV is the maximum spending limit for each AC. ACRT is the revenue target assigned to each AC. Both values are aligned with the overall BMEV and BTRT.',
    img: acExpanatoryImg2
  },
  {
    title: 'Linking Invoices to MAEs',
    message:
      'Invoices can be allocated to specific MAEs, providing detailed insights into the financial activities of each category.',
    img: acExpanatoryImg3
  }
];

const ACDetailPage = () => {
  const [accountingCenter, setAccountingCenter] = useState<any>(null);
  const [graphViewMode, setGraphViewMode] = useState('percentage');
  const { acId, budgetId, acType } = useParams();
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [explanatoryModal, setExplanatoryModal] = useState(true);

  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [activeInvoiceTab, setActiveInvoiceTab] = useState('All');
  const [activeContentTab, setActiveContentTab] = useState<ContentTab>('invoice');
  const [fetchingAccountingCenter, setFetchingAccountingCenter] = useState(false);
  const [workActivities, setWorkActivities] = useState<WorkActivity[]>([]);
  const [loadingWorkActivities, setLoadingWorkActivities] = useState(false);
  const [totalWorkActivityCount, setTotalWorkActivityCount] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  const acCenter = location.state?.acCenter as any | undefined;
  const budget = location.state?.budget as any | undefined;

  const { can } = useBudgetPermissions({
    budgetId,
    fallbackMembers: budget?.members ?? []
  });
  const canCreateInvoice = can('invoice:create');
  const canEditAccountingCenter = can('accountingCenter:edit');
  const canDeleteAccountingCenter = can('accountingCenter:delete') && !accountingCenter?.is_default;

  const totalBmev = (budget?.bmev || 0) + (budget?.bmev_used || 0);

  const handleInvoiceSelect = (invoice: Invoice & { budget?: any }) => {
    if (budgetId) {
      navigate(`${PAGE.BUDGETS.path}/${budgetId}/invoices/${invoice._id}`, {
        state: { budget, from: 'accountingCenter', acType, acCenter }
      });
    }
  };

  console.log('budget', budget);
  console.log('accountingCenter', accountingCenter);
  console.log('totalBmev', totalBmev);

  // console.log('acType', acType);
  // console.log('budgetId', budgetId);
  // console.log('acId', acId);

  const fetchAccountingCenter = async () => {
    setFetchingAccountingCenter(true);
    try {
      const response = await accountingCenterService.getAccountingCenter(budgetId || '', acId || '');
      setAccountingCenter(response.data);
    } catch (error) {
      console.error('Error fetching accounting center:', error);
    } finally {
      setFetchingAccountingCenter(false);
    }
  };

  const fetchBudgetInvoices = async () => {
    if (acId) {
      try {
        const { data: invoicesData } = await invoiceService.getInvoices({
          page: 1,
          limit: 12,
          ac_id: acId,
          invoice_category: activeInvoiceTab === 'All' ? undefined : activeInvoiceTab.toUpperCase(),
          type: undefined
        });
        const filteredInvoices =
          invoicesData.data && invoicesData.data.length > 0
            ? invoicesData.data
                .filter((invoice: Invoice) => invoice.is_draft === false)
                .map((invoice: Invoice) => withPaymentAmountFromAcAllocation(invoice, acId, acType))
            : [];
        setInvoices(filteredInvoices);
      } catch (error) {
        console.error('Error fetching ac invoices:', error);
      } finally {
        setLoadingInvoices(false);
      }
    }
  };

  const fetchACWorkActivities = async () => {
    if (!budgetId || !acId) return;
    try {
      setLoadingWorkActivities(true);
      const response = await workActivityService.getWorkActivities(budgetId, {
        accounting_center_id: acId,
        type: 'all',
        page: 1,
        limit: 8
      });
      setWorkActivities(Array.isArray(response.data) ? response.data : []);
      setTotalWorkActivityCount(response.totalCount ?? 0);
    } catch (error) {
      console.error('Error fetching AC work activities:', error);
      setWorkActivities([]);
      setTotalWorkActivityCount(0);
    } finally {
      setLoadingWorkActivities(false);
    }
  };

  const generalInfoMetrics = getACGeneralInfoMetrics(accountingCenter ?? {}, budget);
  const overviewProgressMetrics = useMemo(
    () => getACOverviewProgressMetrics(accountingCenter ?? {}),
    [accountingCenter]
  );

  const isPercentageView = graphViewMode === 'percentage';

  useEffect(() => {
    fetchAccountingCenter();
  }, []);

  useEffect(() => {
    fetchBudgetInvoices();
  }, [activeInvoiceTab, acId, acType]);

  useEffect(() => {
    fetchACWorkActivities();
  }, [acId]);

  const handleDeleteBankAccount = async () => {
    try {
      await accountingCenterService.deleteAccountingCenter(budgetId || '', acId || '');
      toast.success('Accounting center deleted successfully');
      navigate(-1);
    } catch (error) {
      console.error('Error deleting accounting center:', error);
    }
  };

  return (
    <>
      <MobileAppBar
        title={<span className="capitalize">{acType?.split('_')[0]} Overview</span>}
        onRightIconClick={() => {
          setCurrentStep(0);
          setIsOpen(true);
        }}
        rightIcon={infoIcon}
        rightTwoIcon={canDeleteAccountingCenter ? binIcon : undefined}
        onRightTwoIconClick={canDeleteAccountingCenter ? () => setDeleteConfirmModal(true) : undefined}
      />
      <div className="flex-1 flex flex-col gap-8 md:gap-4 p-4 md:p-0">
        <div className="flex flex-col-reverse md:flex-row gap-8 md:gap-4">
          <div className="hidden md:border md:flex flex-col flex-1 p-4 md:bg-white md:rounded-[16px]">
            {/* Web View Page Header */}
            <header className="hidden md:block">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <DepartmentACIcon />
                </div>

                <div className="flex items-center gap-3">
                  {canEditAccountingCenter && (
                    <button
                      type="button"
                      onClick={() => {
                        navigate(`/budgets/${budgetId}/accounting-centers/${acType}/edit/${acId}`, {
                          state: { acCenter }
                        });
                      }}
                      title="Edit"
                    >
                      <img src={editIcon} alt="edit-icon" width={28} height={28} />
                    </button>
                  )}
                  <button
                    title="Info"
                    onClick={() => {
                      setCurrentStep(0);
                      setIsOpen(true);
                    }}
                  >
                    <img className="w-[28px] h-[28px]" src={infoIcon} alt="vault icon" />
                  </button>
                  {canDeleteAccountingCenter && (
                    <button type="button" onClick={() => setDeleteConfirmModal(true)} title="Delete">
                      <img src={binIcon} alt="bin-icon" width={28} height={28} />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-[24px] font-semibold mt-2">
                {fetchingAccountingCenter ? 'Loading...' : accountingCenter?.name}
              </p>
            </header>

            {/* Web View General Information */}
            <div className="hidden md:block border border-primary rounded-[16px] p-4 bg-lightPurple flex-1 space-y-3 mt-3 min-h-[238px]">
              <p className="text-primary font-bold text-base mb-3">General Information</p>

              <div>
                <p className="text-sm text-secondary font-medium leading-[18px]">Description</p>
                <p className="text-base font-medium leading-[24.5px]">
                  {fetchingAccountingCenter ? '...' : accountingCenter?.description || '-'}
                </p>
              </div>

              <div className="flex gap-4">
                <div className="flex-1">
                  <p className="text-sm text-secondary font-medium leading-[18px]">ACMEV</p>
                  <p className="text-base font-medium leading-[24.5px]">
                    {fetchingAccountingCenter ? (
                      '...'
                    ) : (
                      <span>
                        €{generalInfoMetrics.acmevTotal.toLocaleString()}{' '}
                        <span className="text-secondary">({generalInfoMetrics.acmevPercent}% of BMEV)</span>
                      </span>
                    )}
                  </p>
                </div>

                <div className="flex-1">
                  <p className="text-sm text-secondary font-medium leading-[18px]">ACMEV Alerting Threshold</p>
                  <p className="text-base font-medium leading-[24.5px]">
                    {fetchingAccountingCenter ? '...' : accountingCenter?.acmev_alert_threshold}%
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-1">
                  <p className="text-sm text-secondary font-medium leading-[18px]">ACHOV</p>
                  <p className="text-base font-medium leading-[24.5px]">
                    {fetchingAccountingCenter ? (
                      '...'
                    ) : (
                      <span>
                        €{generalInfoMetrics.achov.toLocaleString()}{' '}
                        <span className="text-secondary">({generalInfoMetrics.acohvPercent}% of BHOV)</span>
                      </span>
                    )}
                  </p>
                </div>

                <div className="flex-1">
                  <p className="text-sm text-secondary font-medium leading-[18px]">ACOH</p>
                  <p className="text-base font-medium leading-[24.5px]">
                    {fetchingAccountingCenter ? (
                      '...'
                    ) : (
                      <span>
                        {generalInfoMetrics.acoh.toLocaleString()} Hours{' '}
                        <span className="text-secondary">({generalInfoMetrics.acohPercent}% of BTOH)</span>
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-1">
                  <p className="text-sm text-secondary font-medium leading-[18px]">ACRT</p>
                  <p className="text-base font-medium leading-[24.5px]">
                    {fetchingAccountingCenter ? (
                      '...'
                    ) : (
                      <>
                        €{accountingCenter?.acrt?.toLocaleString()}
                        <span className="text-secondary"> ({generalInfoMetrics.acrtPercent}% of BTRT)</span>
                      </>
                    )}
                  </p>
                </div>

                <div className="flex-1">
                  <p className="text-sm text-secondary font-medium leading-[18px]">ACRT Alerting Threshold</p>
                  <p className="text-base font-medium leading-[24.5px]">
                    {fetchingAccountingCenter ? '...' : accountingCenter?.acrt_alert_threshold}%
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* <div className="border min-w-[418px] p-4"> */}

          {/* Mobile View General Information */}
          <div className="md:hidden">
            <ACGeneralInfoAccordion
              acData={accountingCenter || {}}
              budget={budget}
              isLoading={fetchingAccountingCenter}
            />
          </div>

          {/* GRAPH OVERVIEW */}
          <div className="md:w-[40%] md:bg-white md:p-[0.85rem] md:rounded-[16px] md:border">
            <header className="w-full flex items-center justify-between mb-3 md:mb-4">
              <h3 className="text-base font-bold text-primary md:text-foreground">Graph Overview</h3>
              <Tabs defaultValue="percentage" value={graphViewMode} onValueChange={setGraphViewMode}>
                <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                  <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                    <img
                      src={percentageIcon}
                      alt="percentage-icon"
                      width={13}
                      height={17}
                      className="group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                  <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                    <img
                      src={euroIcon}
                      alt="euro-icon"
                      width={12}
                      height={12}
                      className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </header>
            <div className="flex flex-col gap-3">
              <ACOverviewProgressCard
                legends={[
                  { label: 'Used ACMEV', color: AC_OVERVIEW_PROGRESS_COLORS.acmevUsed },
                  { label: 'Remaining ACMEV', color: AC_OVERVIEW_PROGRESS_COLORS.primary }
                ]}
                displayValue={
                  isPercentageView
                    ? `${overviewProgressMetrics.remainingAcmevPercent}%`
                    : `€${overviewProgressMetrics.acmevRemainingAmount.toLocaleString()}`
                }
                progressValue={overviewProgressMetrics.usedAcmevPercent}
                fillColor={AC_OVERVIEW_PROGRESS_COLORS.acmevUsed}
              />

              <ACOverviewProgressCard
                legends={[
                  { label: 'Achieved ACRT', color: AC_OVERVIEW_PROGRESS_COLORS.acrtAchieved },
                  { label: 'Projected ACRT', color: AC_OVERVIEW_PROGRESS_COLORS.primary }
                ]}
                displayValue={
                  isPercentageView
                    ? `${overviewProgressMetrics.achievedAcrtPercent}%`
                    : `€${overviewProgressMetrics.acrtAchievedAmount.toLocaleString()}`
                }
                progressValue={overviewProgressMetrics.achievedAcrtPercent}
                fillColor={AC_OVERVIEW_PROGRESS_COLORS.acrtAchieved}
              />

              <ACOverviewProgressCard
                legends={[
                  { label: 'Used ACOH', color: AC_OVERVIEW_PROGRESS_COLORS.acohUsed },
                  { label: 'Remaining ACOH', color: AC_OVERVIEW_PROGRESS_COLORS.primary }
                ]}
                displayValue={
                  isPercentageView
                    ? `${overviewProgressMetrics.usedAcohPercent}%`
                    : `${overviewProgressMetrics.acohUsedAmount.toLocaleString()} Hours`
                }
                progressValue={overviewProgressMetrics.usedAcohPercent}
                fillColor={AC_OVERVIEW_PROGRESS_COLORS.acohUsed}
              />
            </div>
          </div>

          {/* Mobile View Page Header */}
          <header className="flex items-center justify-between gap-4 mb-0 md:hidden">
            <p className="flex items-center gap-3">
              <DepartmentACIcon />
              <span className="text-base font-semibold">{accountingCenter?.name || 'Accounting Center'}</span>
            </p>

            {canEditAccountingCenter && (
              <button
                type="button"
                onClick={() => {
                  navigate(`/budgets/${budgetId}/accounting-centers/${acType}/edit/${acId}`, {
                    state: { acCenter }
                  });
                }}
                title="Edit"
              >
                <img src={editIcon} alt="edit-icon" width={28} height={28} />
              </button>
            )}
          </header>
          {/* </div> */}
        </div>

        {/* Web View Recent Invoice */}
        <div className="mt-2 md:mt-0 md:flex-1 md:bg-white md:p-4 md:rounded-[16px] md:border md:border-lightGray md:flex md:flex-col">
          <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <Tabs
              value={activeContentTab}
              onValueChange={(value) => setActiveContentTab(value as ContentTab)}
              className="w-full md:w-auto"
            >
              <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem] w-full grid grid-cols-2 md:inline-flex md:w-auto">
                <TabsTrigger value="invoice" className="group py-[0.3rem] px-3 w-full">
                  <h4 className="flex items-center justify-center md:justify-start gap-2.5">
                    <img src={invoiceIcon} alt="invoice-icon" className="group-data-[state=active]:hidden" />
                    <img src={invoiceWhiteIcon} alt="invoice-icon" className="hidden group-data-[state=active]:block" />
                    <span className="font-bold text-primary group-data-[state=active]:text-white">Recent Invoice</span>
                  </h4>
                </TabsTrigger>
                <TabsTrigger value="work-activity" className="group py-[0.3rem] px-3 w-full">
                  <h4 className="flex items-center justify-center md:justify-start gap-2.5">
                    <img src={workActivityIcon} alt="work-activity-icon" className="group-data-[state=active]:hidden" />
                    <img
                      src={workActivityWhiteIcon}
                      alt="work-activity-icon"
                      className="hidden group-data-[state=active]:block"
                    />
                    <span className="font-bold text-primary group-data-[state=active]:text-white">Work Activity</span>
                  </h4>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {activeContentTab === 'invoice' && invoices.length > 0 && (
              <Button
                variant="text"
                onClick={() => navigate(`/budgets/${budgetId}/invoices`)}
                className="hidden md:inline-flex py-0 h-[30px] shrink-0"
              >
                See All
              </Button>
            )}
            {activeContentTab === 'work-activity' && totalWorkActivityCount > 7 && (
              <Button
                variant="text"
                onClick={() => navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity`)}
                className="hidden md:inline-flex py-0 h-[30px] shrink-0"
              >
                See All
              </Button>
            )}
          </header>
          {activeContentTab === 'invoice' &&
            (invoices.length > 0 || activeInvoiceTab !== 'All' ? (
              <div className="flex flex-col gap-2 bg-lightPurple rounded-[16px] p-4 py-5 mt-4 md:mt-3 md:p-0 md:bg-white">
                <TabPills
                  options={['All', 'Expense', 'Revenue']}
                  value={activeInvoiceTab}
                  onValueChange={setActiveInvoiceTab}
                />

                <InvoicesTable
                  invoices={invoices.slice(0, 10)}
                  setSelectedInvoice={() => {}}
                  onInvoiceSelect={handleInvoiceSelect}
                />
              </div>
            ) : (
              <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4 md:bg-white md:flex-1">
                <div className="flex flex-col items-center justify-center md:flex-row md:gap-4">
                  <img src={noInvoiceIcon} alt="no invoice icon" className="md:w-[40px] md:h-[40px]" />
                  <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px] md:text-left">
                    <span className="md:text-base md:font-medium md:block mb-[-3px] md:text-foreground">
                      There are no Invoice to show.
                    </span>{' '}
                    <span className="md:text-secondary md:text-base md:font-medium">
                      {' '}
                      Please create a new invoice to allocate budget!
                    </span>
                  </span>
                </div>
                {canCreateInvoice && (
                  <Button
                    onClick={() =>
                      navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
                        state: { from: 'budget', budgetId }
                      })
                    }
                    variant="primary-reverse"
                    className="rounded-full font-semibold px-5 text-base"
                  >
                    Create an Invoice
                  </Button>
                )}
              </div>
            ))}
          {activeContentTab === 'work-activity' &&
            (workActivities.length > 0 || loadingWorkActivities ? (
              <div className="flex flex-col gap-2 min-w-0 bg-lightPurple rounded-[16px] p-4 py-5 mt-4 md:mt-3 md:p-0 md:bg-white">
                <WorkActivityTable
                  workActivities={workActivities.slice(0, 7)}
                  loading={loadingWorkActivities}
                  budgetId={budgetId}
                  onWorkActivitySelect={(wa) => {
                    if (budgetId) {
                      navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/${wa._id}`);
                    }
                  }}
                />
              </div>
            ) : (
              <div className="bg-lightPurple rounded-[16px] p-6 flex items-center justify-center flex-col gap-3 mt-4 md:bg-white md:flex-1">
                <div className="flex flex-col items-center justify-center md:flex-row md:gap-4">
                  <img src={noWorkActivityIcon} alt="no work activity icon" className="md:w-[40px] md:h-[40px]" />
                  <span className="mt-[3px] text-xs font-medium text-center leading-[16.5px] md:text-left">
                    <span className="md:text-base md:font-medium md:block mb-[-3px] md:text-foreground">
                      There are no work activity to show.
                    </span>{' '}
                    <span className="md:text-secondary md:text-base md:font-medium">
                      You can create work activity to request a payment
                    </span>
                  </span>
                </div>
                <Button
                  onClick={() =>
                    navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/create`, {
                      state: { from: 'budget', budgetId }
                    })
                  }
                  variant="primary-reverse"
                  className="rounded-full font-semibold px-5 text-base"
                >
                  Create a Work Activity
                </Button>
              </div>
            ))}
        </div>
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmModal && (
        <InfoModal
          isOpen={deleteConfirmModal}
          variant="confirm"
          title="Are you sure want to Delete this Accounting Center?"
          message={<p>Deleted Department ACs will removed permanently and unable to restore</p>}
          buttonText="Delete"
          onButtonClick={handleDeleteBankAccount}
          onClose={() => setDeleteConfirmModal(false)}
          icon={deleteBankAccountIcon}
        />
      )}

      {/* Explanatory Modal */}
      {isOpen && (
        <ExplanatoryModal
          isOpen={isOpen}
          steps={steps}
          currentStep={currentStep}
          buttonText="Next"
          onButtonClick={() => {
            setCurrentStep(currentStep + 1);
          }}
          onClose={() => {
            setIsOpen(false);
          }}
          onStepChange={setCurrentStep}
        />
      )}
    </>
  );
};

export default ACDetailPage;
